import { medium, low, superLow, mediumHigh } from '@utils'
import isEmpty from 'lodash/isEmpty'
import { isSecondary, isShared, getSpaceUsageColor, isHoteling, isPrimary } from '@utils'
import drawArc from './drawArc'
import drawText from './drawText'
import inView from './inView'
import getSpaceRadius from './getSpaceRadius'
import initials from './initials'

export const drawSpace = (props) => {
  const {
    context,
    x,
    y,
    r,
    selected,
    employee,
    deptColor,
    teamColor,
    dotColor,
    force,
    mobile,
    interactive,
    disabled,
    space,
    isDirectBookingFlow,
  } = props

  if (!force.isCirclesEnabled) {
    return null
  }

  // Gray shadow
  if (r > 10 && !mobile) {
    drawArc({
      cx: x,
      cy: y,
      r: r + 0.2 * r,
      start: 0,
      end: 2 * Math.PI,
      fillStyle: !isDirectBookingFlow
        ? '#CED1D7'
        : space && space.isAvailable
        ? '#CED1D7'
        : '#B1B5BE',
      context,
    })

    // White border
    drawArc({
      cx: x,
      cy: y,
      r: r + 0.1 * r,
      start: 0,
      end: 2 * Math.PI,
      fillStyle: !isDirectBookingFlow ? 'white' : space && space.isAvailable ? 'white' : '#CED1D7',
      context,
    })
  }

  if (!mobile) {
    if (!teamColor || r < 10) {
      // Circle with dept color or #333
      drawArc({
        cx: x,
        cy: y,
        r,
        start: 0,
        end: 2 * Math.PI,
        fillStyle: deptColor,
        context,
      })
    } else {
      // Dept slice
      drawArc({
        cx: x,
        cy: y,
        r,
        start: (3 * Math.PI) / 4,
        end: (7 * Math.PI) / 4,
        fillStyle: deptColor,
        context,
      })

      // Team slice
      drawArc({
        cx: x,
        cy: y,
        r,
        start: (3 * Math.PI) / 4,
        end: (7 * Math.PI) / 4,
        fillStyle: teamColor,
        invert: true,
        context,
      })
    }

    if (dotColor) {
      drawArc({
        cx: x,
        cy: y,
        r: r / 2.2,
        start: 0,
        end: 2 * Math.PI,
        fillStyle: dotColor,
        // strokeStyle: 'white',
        // lineWidth: r / 10,
        context,
      })
    } else if (!employee) {
      // Empty seat indicator
      drawArc({
        cx: x,
        cy: y,
        r: r / 2.2,
        start: 0,
        end: 2 * Math.PI,
        fillStyle: 'white',
        context,
      })
    }

    if (selected && !interactive) {
      // Draw blue circle
      drawArc({
        cx: x,
        cy: y,
        r: 1.5 * r,
        start: 0,
        end: 2 * Math.PI,
        strokeStyle: !isDirectBookingFlow && '#1B4BC5',
        lineWidth: Math.min(r / 2.2, 4),
        context,
      })
    }
  } else {
    if (mobile) {
      if (selected) {
        drawArc({
          cx: x,
          cy: y,
          r,
          start: 0,
          end: 2 * Math.PI,
          fillStyle: '#1688D5',
          context,
        })
      } else if (disabled) {
        drawArc({
          cx: x,
          cy: y,
          r,
          start: 0,
          end: 2 * Math.PI,
          fillStyle: 'gray',
          context,
        })
      } else {
        drawArc({
          cx: x,
          cy: y,
          r,
          start: 0,
          end: 2 * Math.PI,
          fillStyle: dotColor,
          context,
        })
        drawArc({
          cx: x,
          cy: y,
          r: r / 2.2,
          start: 0,
          end: 2 * Math.PI,
          fillStyle: 'white',
          context,
        })
      }
    }
  }
}

export const getColorByStatus = (status) => (status === 'available' ? '#1B4BC5' : '#CED1D7')

const drawSafeDistancingCircle = (props) => {
  const { context, x, y, r, force, overlapping } = props

  if (!force.isCirclesEnabled) {
    return null
  }

  drawArc({
    cx: x,
    cy: y,
    r,
    start: 0,
    end: 2 * Math.PI,
    fillStyle: overlapping ? 'rgba(249, 215, 73, 0.2)' : 'rgba(24, 144, 255, 0.1)',
    context,
  })
}

const drawName = (props) => {
  const {
    context,
    x,
    y,
    r,
    t,
    employee,
    employees,
    fontSize,
    density,
    maxScale,
    secondaryseat,
    shareddesk,
    force,
    spaceLabel,
  } = props

  const empl = employee ||
    (shareddesk && employees.length === 1 ? employees[0] : null) || { name: '' }

  if (spaceLabel || empl) {
    let text = ''
    let fillStyle = 'white'
    let textBaseline = 'middle'

    let breakPoint = maxScale / 2

    if (density === 0) {
      breakPoint = 1
    } else if (superLow(density)) {
      breakPoint = 2
    } else if (low(density)) {
      breakPoint = maxScale / 7
    } else if (medium(density)) {
      breakPoint = maxScale / 4
    }

    if (force.isEmployeeNameEnabled || force.isPhoneNumberEnabled || t.k >= breakPoint) {
      fillStyle = '#333'
      textBaseline = 'top'

      let tokens = []

      if ((empl && force.isEmployeeNameEnabled) || t.k >= breakPoint) {
        tokens = empl.name.split(/\s/).reduce((result, s, i, arr) => {
          if (i === 0) {
            return [s]
          } else if (i === arr.length - 1) {
            return [...result, s]
          }
          return result
        }, [])
      }

      if (!isEmpty(spaceLabel)) {
        tokens.unshift(spaceLabel)
      }

      tokens.forEach((token, i) =>
        drawText({
          x,
          y: y + i * fontSize + (force.isCirclesEnabled ? 1.2 * r : 0),
          text: token,
          fillStyle: '#333',
          textBaseline,
          fontSize,
          context,
          center: true,
        })
      )

      // Render phone number for low and medium desnity spaces
      if (empl.phone) {
        if (
          force.isPhoneNumberEnabled ||
          (t.k > maxScale / 4 && (density === 0 || superLow(density) || low(density))) ||
          (medium(density) && t.k > maxScale / 2)
        ) {
          drawText({
            x,
            y: y + tokens.length * fontSize + (force.isCirclesEnabled ? 1.2 * r : 0),
            text: empl.phone,
            fillStyle,
            textBaseline,
            fontSize,
            context,
            center: true,
          })
        }
      }
    } else if (r > 10) {
      if (employee && !secondaryseat && !shareddesk) {
        text = initials(employee.name)
        drawText({
          x,
          y,
          text,
          fillStyle,
          textBaseline,
          fontSize: r * 0.9,
          context,
          center: true,
          shadow: false,
        })
      }
    }
  } else if (shareddesk && employees && employees.length) {
    const text = `${employees.length} employees`

    let breakPoint = maxScale / 2

    if (density === 0) {
      breakPoint = 1
    } else if (superLow(density)) {
      breakPoint = 2
    } else if (low(density)) {
      breakPoint = maxScale / 7
    } else if (medium(density)) {
      breakPoint = maxScale / 4
    }

    if (force.isEmployeeNameEnabled || t.k >= breakPoint) {
      drawText({
        x,
        y: y + (force.isCirclesEnabled ? 1.2 * r : 0),
        text,
        fillStyle: '#333',
        textBaseline: 'top',
        fontSize,
        context,
        center: true,
      })
    }
  }
}

const drawSpaceCode = (props) => {
  const { context, x, y, r, fontConfig, code, mapViewSettings } = props
  drawText({
    x,
    y: y - (mapViewSettings.sCircle === 'On' ? 1.2 * r : 0),
    text: code,
    fillStyle: '#333',
    fontWeight: '500',
    textBaseline: 'bottom',
    fontSize: fontConfig.sCode,
    context,
    center: true,
  })
}

const Seatable = ({
  context,
  space,
  dx,
  dy,
  maxScale,
  xScale,
  yScale,
  rScale,
  transform: t,
  selected,
  filtered,
  screen,
  fontSize,
  draggedItem,
  canDrag,
  isOver,
  preview,
  print,
  liveModeOn,
  overlapping,
  pixelsPerFoot,
  safeDistance,
  showSafeDistanceCircle,
  force = {
    isCirclesEnabled: true,
    isSpaceCodesEnabled: false,
    isEmployeeNameEnabled: false,
    isPhoneNumberEnabled: false,
    isOtherSpacesIconsEnabled: true,
    isOtherSpacesNamesEnabled: true,
  },
  mobile,
  interactive,
  isDirectBookingFlow,
  disabled,
  hideSpaceCode,
  mapViewSettings,
  fontConfig,
}) => {
  let dept = space && space.spaceAssignment && space.spaceAssignment.department
  let team = space && space.spaceAssignment && space.spaceAssignment.team
  const employee = space && space.spaceAssignment && space.spaceAssignment.employee
  const employees = (space && space.spaceAssignment && space.spaceAssignment.sharedEmployees) || []
  const secondaryseat = isSecondary(space.usageType)
  const shareddesk = isShared(space.usageType)

  let dotColor = getSpaceUsageColor(space.usageType || {})

  const x = dx * t.k + xScale(space.x)
  const y = dy * t.k + yScale(space.y)
  let r = getSpaceRadius({
    space,
    k: t.k,
    rScale,
    isOver,
    filtered,
    maxScale,
  })

  const getSFRadius = ({ k, rScale, safeDistance, pixelsPerFoot }) => {
    const w = safeDistance * pixelsPerFoot
    const r = k * rScale(w)

    return r
  }

  const safeDistanceCircleRadius = getSFRadius({
    safeDistance,
    pixelsPerFoot,
    k: t.k,
    rScale,
  })

  if (print && selected) {
    r = Math.min(25, r * 1.5)
    force.isEmployeeNameEnabled = true
  }

  if (t.k >= 3 && !inView({ x, y, screen })) {
    return
  }

  context.globalAlpha = 1
  let spaceBorderColor

  if ((!selected && filtered) || preview) {
    context.globalAlpha = 0.5
  } else if (draggedItem && !canDrag) {
    context.globalAlpha = 0.5
  }

  if (liveModeOn && space.status) {
    drawSpace({
      context,
      x,
      y,
      r,
      t,
      selected,
      employee,
      employees,
      deptColor: getColorByStatus(space.status),
      density: space.density,
      dotColor: getColorByStatus(space.status),
      force,
      space,
      isDirectBookingFlow,
    })

    if (showSafeDistanceCircle) {
      drawSafeDistancingCircle({
        context,
        x,
        y,
        r: safeDistanceCircleRadius,
        force,
        overlapping,
      })
    }
  } else {
    if (
      (isDirectBookingFlow && space.dailyBookingAvailability) ||
      space.dailyBookableSpacesAvailability ||
      (interactive && mobile)
    ) {
      spaceBorderColor = space.isAvailable ? '#1B4BC5' : '#CED1D7'
      dept = null
      team = null
      dotColor = isHoteling(space.usageType)
        ? space.isAvailable
          ? '#1B4BC5'
          : '#B1B5BE'
        : isPrimary(space.usageType)
        ? mobile
          ? '#000'
          : space.isAvailable
          ? '#fff'
          : '#B1B5BE'
        : space.isAvailable
        ? getSpaceUsageColor(space.usageType || {})
        : '#B1B5BE'
    }
    drawSpace({
      context,
      x,
      y,
      r,
      t,
      selected,
      employee,
      employees,
      deptColor: isDirectBookingFlow ? spaceBorderColor : dept ? `#${dept.color}` : '#333',
      teamColor: team ? `#${team.color}` : null,
      density: space.density,
      dotColor,
      force,
      mobile,
      interactive,
      disabled,
      space,
      isDirectBookingFlow,
    })

    if (showSafeDistanceCircle) {
      drawSafeDistancingCircle({
        context,
        x,
        y,
        r: safeDistanceCircleRadius,
        force,
        overlapping,
      })
    }
  }

  drawName({
    context,
    x,
    y,
    r,
    t,
    employee,
    employees,
    fontSize,
    maxScale,
    density: space.density,
    secondaryseat,
    shareddesk,
    force,
    spaceLabel: space.name,
  })

  fontConfig.sCode &&
    drawSpaceCode({
      context,
      x,
      y,
      r,
      t,
      isOver,
      fontConfig,
      maxScale,
      preview,
      mapViewSettings,
      code: space.code,
    })

  context.globalAlpha = 1
}

export default Seatable
